import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { isLowStock } from "@/supplies"
import { ItemForm, type ItemFormValues } from "@/supplies/components/item-form"
import {
  StockActionDialog,
  type StockActionSubmit,
} from "@/supplies/components/stock-action-dialog"
// Alias useSupplyStockFn: name starts with "use" and trips rules-of-hooks lint.
import {
  useSupplyStockFn as consumeSupplyStockFn,
  createSupplyItemFn,
  ensureDefaultCategoriesFn,
  listSupplyCategoriesFn,
  listSupplyItemsFn,
  updateSupplyItemFn,
} from "@/supplies/rpc"
import { Badge } from "@/ui/badge"
import { Button } from "@/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"
import { cn } from "@/ui/utils"

export const Route = createFileRoute("/_supplies-layout/supplies/items")({
  component: ItemsPage,
})

interface ItemRow {
  id: number
  name: string
  sku: string | null
  categoryId: number
  unit: string
  quantityOnHand: number
  reorderLevel: number | null
  unitCostCents: number | null
  notes: string | null
  isActive: boolean
  category?: { id: number; name: string } | null
}

interface FilterState {
  search: string
  categoryId: string
  lowStockOnly: boolean
  /** "active" | "inactive" | "all" */
  status: "active" | "inactive" | "all"
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—"
  return `$${(cents / 100).toFixed(2)}`
}

function selectClassName() {
  return cn(
    "flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] md:text-sm dark:bg-input/30",
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "disabled:cursor-not-allowed disabled:opacity-50",
  )
}

function ItemsPage() {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ItemRow | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [useTarget, setUseTarget] = useState<ItemRow | null>(null)
  const [useError, setUseError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    categoryId: "",
    lowStockOnly: false,
    status: "active",
  })

  const seedQuery = useQuery({
    queryKey: ["supply-categories", "ensure-defaults"],
    queryFn: () => ensureDefaultCategoriesFn(),
  })

  const categoriesQuery = useQuery({
    queryKey: ["supply-categories", { includeInactive: true }],
    queryFn: () => listSupplyCategoriesFn({ data: { includeInactive: true } }),
    enabled: seedQuery.isSuccess,
  })

  const listFilters = useMemo(() => {
    const categoryId = filters.categoryId
      ? Number(filters.categoryId)
      : undefined
    return {
      search: filters.search.trim() || undefined,
      categoryId:
        categoryId != null && Number.isFinite(categoryId)
          ? categoryId
          : undefined,
      lowStockOnly: filters.lowStockOnly || undefined,
      pageSize: 200,
    }
  }, [filters])

  // Server defaults isActive to true, so "all" merges active + inactive queries.
  const itemsQuery = useQuery({
    queryKey: ["supply-items", filters.status, listFilters],
    queryFn: async () => {
      if (filters.status === "all") {
        const [active, inactive] = await Promise.all([
          listSupplyItemsFn({
            data: { ...listFilters, isActive: true },
          }),
          listSupplyItemsFn({
            data: { ...listFilters, isActive: false },
          }),
        ])
        const merged = [...active.items, ...inactive.items].sort((a, b) =>
          a.name.localeCompare(b.name),
        )
        return {
          items: merged,
          total: active.total + inactive.total,
          page: 1,
          pageSize: merged.length,
        }
      }
      return listSupplyItemsFn({
        data: {
          ...listFilters,
          isActive: filters.status === "active",
        },
      })
    },
  })

  const createItem = useMutation({
    mutationFn: (values: ItemFormValues) =>
      createSupplyItemFn({
        data: {
          name: values.name,
          sku: values.sku,
          categoryId: values.categoryId,
          unit: values.unit,
          reorderLevel: values.reorderLevel,
          unitCostCents: values.unitCostCents,
          notes: values.notes,
        },
      }),
    onSuccess: async () => {
      setFormError(null)
      setDialogOpen(false)
      setEditing(null)
      await queryClient.invalidateQueries({ queryKey: ["supply-items"] })
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const updateItem = useMutation({
    mutationFn: (input: { id: number; values: ItemFormValues }) =>
      updateSupplyItemFn({
        data: {
          id: input.id,
          name: input.values.name,
          sku: input.values.sku,
          categoryId: input.values.categoryId,
          unit: input.values.unit,
          reorderLevel: input.values.reorderLevel,
          unitCostCents: input.values.unitCostCents,
          notes: input.values.notes,
          isActive: input.values.isActive,
        },
      }),
    onSuccess: async () => {
      setFormError(null)
      setDialogOpen(false)
      setEditing(null)
      await queryClient.invalidateQueries({ queryKey: ["supply-items"] })
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const useStock = useMutation({
    mutationFn: (input: {
      itemId: number
      quantity: number
      note: string | null
    }) =>
      consumeSupplyStockFn({
        data: {
          itemId: input.itemId,
          quantity: input.quantity,
          note: input.note,
        },
      }),
    onSuccess: async () => {
      setUseError(null)
      setUseTarget(null)
      await queryClient.invalidateQueries({ queryKey: ["supply-items"] })
      await queryClient.invalidateQueries({ queryKey: ["supply-movements"] })
      await queryClient.invalidateQueries({ queryKey: ["supplies-dashboard"] })
    },
    onError: (error) => setUseError(getErrorMessage(error)),
  })

  const isSubmitting = createItem.isPending || updateItem.isPending
  const isUsing = useStock.isPending
  const items = (itemsQuery.data?.items ?? []) as ItemRow[]

  function openCreate() {
    setEditing(null)
    setFormError(null)
    setDialogOpen(true)
  }

  function openEdit(item: ItemRow) {
    setEditing(item)
    setFormError(null)
    setDialogOpen(true)
  }

  function openUse(item: ItemRow) {
    setUseError(null)
    setUseTarget(item)
  }

  function closeDialog() {
    if (isSubmitting) return
    setDialogOpen(false)
    setEditing(null)
    setFormError(null)
  }

  async function handleSubmit(values: ItemFormValues) {
    if (editing) {
      await updateItem.mutateAsync({ id: editing.id, values })
    } else {
      await createItem.mutateAsync(values)
    }
  }

  async function handleUseSubmit(payload: StockActionSubmit) {
    if (isUsing || useTarget == null || payload.quantity == null) return
    setUseError(null)
    try {
      await useStock.mutateAsync({
        itemId: useTarget.id,
        quantity: payload.quantity,
        note: payload.note ?? null,
      })
    } catch {
      // surfaced via onError
    }
  }

  const listError = itemsQuery.isError
    ? getErrorMessage(itemsQuery.error)
    : categoriesQuery.isError
      ? getErrorMessage(categoriesQuery.error)
      : seedQuery.isError
        ? getErrorMessage(seedQuery.error)
        : null

  const hasFilters =
    Boolean(filters.search.trim()) ||
    Boolean(filters.categoryId) ||
    filters.lowStockOnly ||
    filters.status !== "active"

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-semibold text-2xl tracking-tight">Items</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            Catalog of office and store supply items.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          New item
        </Button>
      </div>

      <div className="space-y-3 rounded-md border p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1.5">
            <Label htmlFor="items-search">Search</Label>
            <Input
              id="items-search"
              value={filters.search}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  search: event.target.value,
                }))
              }
              placeholder="Name or SKU"
              autoComplete="off"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="items-category">Category</Label>
            <select
              id="items-category"
              value={filters.categoryId}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  categoryId: event.target.value,
                }))
              }
              className={selectClassName()}
            >
              <option value="">All categories</option>
              {(categoriesQuery.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                  {!category.isActive ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="items-status">Status</Label>
            <select
              id="items-status"
              value={filters.status}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  status: event.target.value as FilterState["status"],
                }))
              }
              className={selectClassName()}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="flex items-end gap-3">
            <label className="flex h-9 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.lowStockOnly}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    lowStockOnly: event.target.checked,
                  }))
                }
                className="size-4 rounded border border-input"
              />
              Low stock only
            </label>
            {hasFilters ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters({
                    search: "",
                    categoryId: "",
                    lowStockOnly: false,
                    status: "active",
                  })
                }
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {listError ? (
        <p className="text-destructive text-sm" role="alert">
          {listError}
        </p>
      ) : null}

      {itemsQuery.isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No items match these filters.
          {!hasFilters ? " Create your first item to get started." : null}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">SKU</th>
                <th className="px-3 py-2 font-medium">Category</th>
                <th className="px-3 py-2 font-medium">On hand</th>
                <th className="px-3 py-2 font-medium">Reorder</th>
                <th className="px-3 py-2 font-medium">Unit cost</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((item) => {
                const low = isLowStock(item)
                return (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td
                      className={cn(
                        "px-3 py-2 font-medium",
                        !item.isActive && "text-muted-foreground",
                      )}
                    >
                      <Link
                        to="/supplies/items/$itemId"
                        params={{ itemId: String(item.id) }}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {item.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-muted-foreground text-xs">
                      {item.sku || "—"}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {item.category?.name ?? "—"}
                    </td>
                    <td className="px-3 py-2 tabular-nums">
                      {item.quantityOnHand} {item.unit}
                      {low ? (
                        <Badge variant="destructive" className="ml-2">
                          Low
                        </Badge>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground tabular-nums">
                      {item.reorderLevel ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground tabular-nums">
                      {formatCents(item.unitCostCents)}
                    </td>
                    <td className="px-3 py-2">
                      {item.isActive ? (
                        <Badge variant="secondary">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {item.isActive ? (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => openUse(item)}
                          >
                            Use
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(item)}
                        >
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {itemsQuery.data != null && itemsQuery.data.total > items.length ? (
        <p className="text-muted-foreground text-xs">
          Showing {items.length} of {itemsQuery.data.total} items.
        </p>
      ) : null}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog()
          else setDialogOpen(true)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit item" : "New item"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update item details or activate/deactivate it."
                : "Add a supply item to the catalog."}
            </DialogDescription>
          </DialogHeader>
          <ItemForm
            key={editing ? `edit-${editing.id}` : "create"}
            mode={editing ? "edit" : "create"}
            categories={categoriesQuery.data ?? []}
            initialValues={
              editing
                ? {
                    name: editing.name,
                    sku: editing.sku,
                    categoryId: editing.categoryId,
                    unit: editing.unit,
                    reorderLevel: editing.reorderLevel,
                    unitCostCents: editing.unitCostCents,
                    notes: editing.notes,
                    isActive: editing.isActive,
                  }
                : undefined
            }
            isSubmitting={isSubmitting}
            error={formError}
            onSubmit={handleSubmit}
            onCancel={closeDialog}
          />
        </DialogContent>
      </Dialog>

      {useTarget != null ? (
        <StockActionDialog
          open
          onOpenChange={(open) => {
            if (!open && !isUsing) {
              setUseTarget(null)
              setUseError(null)
            }
          }}
          mode="use"
          itemName={useTarget.name}
          quantityOnHand={useTarget.quantityOnHand}
          isSubmitting={isUsing}
          error={useError}
          onSubmit={handleUseSubmit}
        />
      ) : null}
    </div>
  )
}
